<template>
  <div>
    <refonte-modal
      v-bind:show="props.show"
      v-bind:no_outside_click="completed && result && !result.reussi"
      v-bind:no_close_btn="completed && result && !result.reussi"
      class="!max-w-[1200px]"
      v-on:close="openModalAlertClose">
      <template v-slot:modalHeader>
        <h1>{{ formation.titre }}</h1>
        <div class="item-file-content-title-text-bottom">
          Test de positionnement
        </div>
      </template>

      <template v-slot:modalBody>
        <template v-if="loading">
          <spinner
            v-bind:sizes="'w-20 h-20'"
            v-bind:thickness="'border-8'" />
        </template>

        <template v-else>
          <div
            v-if="result"
            class="px-4">
            <div class="my-4 text-2xl font-bold">Résultat</div>

            <template v-if="result.reussi">
              <message-box
                v-bind:source="
                  result.texteSiAcquis ||
                  'Vous avez réussi le test de positionnement, votre niveau de connaissance est suffisant.'
                "
                v-bind:border_color="'w-full border-yellow-500'"
                v-bind:font_style="'text-lg'" />
            </template>

            <template v-else>
              <message-box
                v-bind:source="
                  result.texteSiNonAcquis ||
                  'Vous n’avez pas réussi le test de positionnement.'
                "
                v-bind:border_color="'w-full border-jinius-red'"
                v-bind:font_style="'text-lg'" />
              <p class="label-text">
                J'ai compris que je ne disposais pas, à priori, des
                connaissances suffisantes pour suivre cette formation.
              </p>
              <br />

              <div>
                <label class="label-text">
                  <input
                    type="radio"
                    id="radio-continuer"
                    name="choix-annulation"
                    v-bind:value="false"
                    v-model="demandeAnnulation" />
                  Je souhaite maintenir ma demande d'inscription à la formation,
                  et je m'engage à acquérir les compétences nécessaires pour
                  suivre cette formation.
                </label>

                <label class="label-text">
                  <input
                    type="radio"
                    id="radio-annuler"
                    name="choix-annulation"
                    v-bind:value="true"
                    v-model="demandeAnnulation" />
                  Je souhaite annuler ma demande d'inscription à la formation.
                </label>
              </div>
            </template>
          </div>

          <div
            v-else
            class="flex w-full flex-wrap space-x-4 px-8 py-4 md:space-x-0 md:space-y-4">
            <div class="flex w-full flex-wrap">
              <div class="block w-full">
                <surveyjs-wrapper
                  ref="surveyWrapper"
                  v-on:complete="complete"
                  v-bind:json="surveyText" />
              </div>
            </div>
          </div>
        </template>
      </template>

      <template v-slot:modalFooter>
        <div
          v-if="surveyCatcher"
          class="inline-flex w-full justify-end space-x-4 text-lg">
          <refonte-button
            v-if="!surveyCatcher.isFirstPage && !result"
            v-bind:on_click="() => surveyWrapper.prevPage()">
            Précédent
          </refonte-button>

          <refonte-button
            v-if="!surveyCatcher.isLastPage && !result"
            v-bind:on_click="() => surveyWrapper.nextPage()">
            Suivant
          </refonte-button>

          <refonte-button
            v-if="surveyCatcher.isLastPage && !result"
            v-bind:on_click="() => surveyWrapper.completeLastPage()">
            Terminer
          </refonte-button>

          <refonte-button
            v-else-if="result"
            v-bind:disabled="demandeAnnulation === null && !result.reussi"
            v-bind:on_click="handleValidation">
            Valider et fermer
          </refonte-button>
        </div>
      </template>
    </refonte-modal>

    <satisfaction-alert-modal-close
      v-bind:show="isModalAlertCloseOpen"
      v-bind:on_close="closeModalAlertClose"
      v-bind:on_submit="close" />
  </div>
</template>

<script setup lang="ts">
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import { onMounted, ref, watchEffect } from 'vue';
  import SatisfactionAlertModalClose from '@/views/_espace/_refonte/gestion/_satisfaction/satisfaction-alert-modal-close.vue';
  import { FormationResponse } from '@/types/participant/formations-types';
  import { API } from '@/http-common';
  import SurveyjsWrapper from '@/components/utils/surveyjs-wrapper.vue';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import MessageBox from '@/components/utils/message-box.vue';
  import Spinner from '@/components/utils/spinner.vue';

  const props = withDefaults(
    defineProps<{
      show: boolean;
      on_close: Function;
      formation: FormationResponse;
      on_complete?: Function;
    }>(),
    {}
  );
  const completed = ref<boolean>(false);
  const detail = ref(null);
  const loading = ref(false);
  const surveyText = ref({});
  const surveyCatcher = ref(null);
  const surveyWrapper = ref(null);
  const isModalAlertCloseOpen = ref<boolean>(false);
  const result = ref(null);
  const demandeAnnulation = ref<string | null>(null);
  const multiple = ref(false);
  const participantType = ref('');
  const participantId = ref<string | number | null>(null);

  const computeParticipantIdentifiers = () => {
    const typeKeyMap = new Map<number, string>([
      [8, 'cursusInscriptionId'],
      [2, 'elearningParticipantId']
    ]);

    const participantKey =
      typeKeyMap.get(props.formation.type.id) ?? 'sessionParticipantId';

    participantType.value = participantKey;
    participantId.value = props.formation[participantKey] ?? props.formation.id;
  };

  onMounted(() => {
    computeParticipantIdentifiers();
    fetchDetail();
  });

  watchEffect(() => {
    if (surveyWrapper.value) {
      surveyCatcher.value = surveyWrapper.value.survey;
    }
  });

  const close = () => {
    detail.value = null;
    completed.value = false;
    props.on_close();
  };
  const openModalAlertClose = () => {
    isModalAlertCloseOpen.value = true;
  };
  const closeModalAlertClose = () => {
    isModalAlertCloseOpen.value = false;
  };

  const fetchDetail = async () => {
    loading.value = true;

    const response = await API.get(
      `participant/evaluation?parcoursProduitId=${props.formation.produitId}&${participantType.value}=${participantId.value}&typeId=1`
    );

    if (response && response.data) {
      surveyText.value = response.data.survey;
      multiple.value = response.data.multiple;
    }

    loading.value = false;
  };

  const complete = async () => {
    loading.value = true;

    const data = surveyCatcher.value?.data || {};

    const response = await API.post(
      `participant/evaluation${multiple.value ? '/multiple' : ''}?parcoursProduitId=${props.formation.produitId}&${participantType.value}=${participantId.value}&typeId=1`,
      data
    );

    if (response && response.data) {
      result.value = response.data;
      completed.value = true;
      if (props.on_complete) {
        props.on_complete();
      }
    }

    loading.value = false;
  };

  const handleValidation = async () => {
    if (demandeAnnulation.value !== null) {
      await API.put(
        `participant/demande-annulation?${participantType.value}=${participantId.value}`,
        demandeAnnulation.value
      );
    }

    close();
  };
</script>

<style lang="scss" scoped>
  .label-text {
    display: block;
    font-size: 1.2rem;
    line-height: 1.5;
    color: #333333;
  }
</style>
